import React from "react"

import { IPublicConversationItem } from "./hooks/public-conversation-item"

import { loads } from "src/channel/utils"
import { DEFAULT_ACCESS_GROUP } from "src/config"

import { Channel } from "src/channel"
import { createLoadingKeys } from "src/lib/loading"
import { Pagination } from "src/lib/pagination"
import { TagsAdminService, tags_Tag } from "src/api"
import { IAdvanceQueryModel } from "src/types/data-grid-pro"
import { ChatbotAdminService } from "src/api/services/ChatbotAdminService"

export class PublicConversationStore implements IDisposable {
    static Context = React.createContext<PublicConversationStore | null>(null)
    static LoadingKeys = createLoadingKeys("init", "loading")
    private repositoryUpdatesListenerDisposer?: () => void

    private _segments: number[] = []
    private _accessGroupId: number = DEFAULT_ACCESS_GROUP.id
    private _allConversationIds: string[] = []
    _tags: tags_Tag[] | undefined

    get segments() {
        return this._segments
    }

    get tags() {
        return this._tags
    }

    get allConversationIds() {
        return this._allConversationIds
    }

    conversationList = new Pagination(
        async (params) => {
            const response =
                await ChatbotAdminService.postV1AdminChatbotPublicConversations(
                    {
                        request: {
                            filter: params.advanceQuery,
                        },
                        pageNumber: params.page,
                        pageSize: params.pageSize,
                        accessGroupId: this.getAccessGroupId(),
                    },
                )

            const items: IPublicConversationItem[] = (
                response.conversations ?? []
            ).map((item) => ({
                id: item.conversation_id ?? "",
                conversation_id: item.conversation_id ?? "",
                resolution: item.resolution ?? "",
                user_feedback_reason: item.user_feedback?.reason ?? "",
                user_feedback_thumb_status:
                    item.user_feedback?.thumb_status ?? -1,
                admin_feedback_reason: item.admin_feedback?.reason ?? "",
                admin_feedback_thumb_status:
                    item.admin_feedback?.thumb_status ?? -1,
                tags: item.tags,
                created_at:
                    item.created_at !== undefined
                        ? new Date(item.created_at)
                        : undefined,
                ended_at: item.ended_at ?? "",
                updated_at: item.updated_at ?? "",
                language: item.language ?? "",
                property_owner_name: item.property_owner_name ?? "",
                no_response_count: item.no_response_count ?? 0,
                is_self_service: item.is_self_service ?? false,
                dispatch_count: item.dispatch_count ?? 0,
                clarification_count: item.clarification_count ?? 0,
            }))

            this.setAllConversationIds(
                items.map((item) => item.conversation_id),
            )

            return { items, count: response.total_count ?? 0 }
        },
        {
            loadingKey: PublicConversationStore.LoadingKeys.loading,
        },
    )

    dispose() {
        this.repositoryUpdatesListenerDisposer?.()
    }

    private listenToRepositoryUpdated() {
        this.repositoryUpdatesListenerDisposer = Channel.addListener(
            async (event) => {
                if (
                    event.name === "repository/updated" &&
                    event.payload.repository === "chatbotConversation"
                ) {
                    await this.conversationList.reload()
                }
            },
        )
    }

    @loads(() => PublicConversationStore.LoadingKeys.init)
    async init(accessGroupId: number, advanceQuery: IAdvanceQueryModel) {
        this.listenToRepositoryUpdated()
        this.setAccessGroupId(accessGroupId)
        await this.conversationList.loadInitialPage(advanceQuery)
        this._tags = await this.getTags()
    }

    async getTags() {
        const response = await TagsAdminService.postV1AdminTagList({
            request: { page_size: 10000 },
        })

        return response.tags
    }

    async query(advanceQuery: IAdvanceQueryModel) {
        await this.conversationList.loadAdvanceQuery(advanceQuery)
    }

    private setSegments(segments: number[]) {
        this._segments = segments
    }

    private setAccessGroupId(accessGroupId: number) {
        this._accessGroupId = accessGroupId
    }

    private getAccessGroupId() {
        return this._accessGroupId !== DEFAULT_ACCESS_GROUP.id
            ? this._accessGroupId
            : undefined
    }

    private setAllConversationIds(allConversationIds: string[]) {
        this._allConversationIds = allConversationIds
    }

    async loadSegments(segments: number[]) {
        this.setSegments(segments)
        await this.conversationList.loadInitialPage()
    }
}
